import React from 'react';
import { useTranslation } from 'react-i18next';
import { usePostHog } from 'posthog-js/react';

// components
import Chatbar from 'eCoach/chatbar/Chatbar';
import ClickableInput from 'eCoach/ClickableInput';
import { PromptOption } from 'eCoach/promptHelper/prompts';

// hooks
import { useUser } from 'shared/hooks/api/user';

// styles
import * as ChatStartStyle from 'eCoach/_styles/ChatStart.style';
import * as StyledPromptControls from 'eCoach/_styles/PromptControls.style';
import { useFormTemplates } from 'shared/hooks/api/formFilling';

interface ChatGuideData {
  promptHelperGoal: PromptOption | string | null;
  promptHelperTheme: string | null;
  chatStartEnded: boolean;
}

interface ChatLandingProps {
  activatePolicyHelper: () => void;
  activateUnionHelper: () => void;
  handleChatGuidance: (data: Partial<ChatGuideData>) => void;
  onSubmit: (text: string) => void;
  openPromptHelper: () => void;
  startFormFilling: () => void;
}

export default function ChatLanding({
  activatePolicyHelper,
  activateUnionHelper,
  handleChatGuidance,
  onSubmit,
  openPromptHelper,
  startFormFilling,
}: ChatLandingProps): JSX.Element {
  const { t } = useTranslation('pageEcoach');
  const { data: user } = useUser();
  const { data: templates } = useFormTemplates();
  const posthog = usePostHog();

  const getEmergencyLink = (): string => {
    if (user.organization?.name === 'Ardene')
      return `/documents/195b172d-f368-4e53-83af-a634027ef450/pdf?language=${user.language}`;

    if (
      user.scopes.some(
        (scope) => scope.full_scope === 'Company > Metro > Site > Terrebonne'
      )
    )
      return `/documents/e029e86e-4cf7-4afb-9fcf-6d6780ae49ff/pdf?language=${user.language}`;

    if (
      user.scopes.some(
        (scope) => scope.full_scope === 'Company > Metro > Region > Canada > ON'
      )
    )
      return `/documents/9ced5d23-0b4b-45d1-963b-98a907bcc49b/pdf?language=${user.language}`;

    return '';
  };

  const emergencyLink = getEmergencyLink();

  const emergencyLinkAction = (): void => {
    window.open(emergencyLink, '_blank');
  };

  const handleSelectedTheme = (selection: string) =>
    handleChatGuidance({ promptHelperTheme: selection });

  const canAccessUnionHelper = posthog.isFeatureEnabled('metro_union');
  const canAccessFormFilling =
    posthog.isFeatureEnabled('enable_form_filling') && templates?.length > 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <ChatStartStyle.Section>
        <StyledPromptControls.ButtonsColumn>
          <ClickableInput
            className="onboarding-tour-step-1"
            inputAction={() =>
              handleSelectedTheme('Preparing for a difficult conversation')
            }
            inputText={t('chatStart.ChatLanding.Prepare for a conversation')}
            data-tour-target="prepare-conversation"
          />
          {canAccessFormFilling && (
            <ClickableInput
              inputAction={startFormFilling}
              inputText={t('chatStart.ChatLanding.Write a warning letter')}
            />
          )}
          <ClickableInput
            inputAction={activatePolicyHelper}
            inputText={t('chatStart.ChatLanding.Get answers about policies')}
          />
          {canAccessUnionHelper && (
            <ClickableInput
              inputAction={activateUnionHelper}
              inputText={t(
                'chatStart.ChatLanding.Get answers about union rules'
              )}
            />
          )}
          <ClickableInput
            inputAction={openPromptHelper}
            inputText={t('chatStart.ChatLanding.Explore other ideas')}
          />
          {emergencyLink.length > 0 && (
            <ClickableInput
              inputAction={emergencyLinkAction}
              inputText={t(
                'chatStart.ChatLanding.Click here in case of emergency'
              )}
            />
          )}
        </StyledPromptControls.ButtonsColumn>
      </ChatStartStyle.Section>
      <Chatbar onSubmit={onSubmit} />
    </div>
  );
}
